import { useEffect, useState } from "react";
import { FiAlertTriangle } from "react-icons/fi";
import { useNavigate, useSearchParams } from "react-router-dom";
import PageContainer from "../components/layout/PageContainer";
import SectionTitle from "../components/common/SectionTitle";
import Input from "../components/common/Input";
import Button from "../components/common/Button";
import * as bookingService from "../services/bookingService";

const BookingCancel = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [code, setCode] = useState(searchParams.get("code") || "");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleCancel = async (event) => {
    event.preventDefault();
    setError("");

    const trimmedCode = code.trim();

    if (!trimmedCode) {
      setError("Please enter your confirmation code.");
      return;
    }

    try {
      setLoading(true);

      const data = await bookingService.cancelBookingByConfirmationCode(trimmedCode);

      if (data?.statusCode && data.statusCode !== 200) {
        throw new Error(data.message || "Unable to cancel booking.");
      }

      navigate("/action-status", {
        replace: true,
        state: {
          status: "success",
          eyebrow: "Booking Canceled",
          title: "Your appointment has been canceled successfully",
          message:
            data?.message ||
            "The appointment linked to this confirmation code has been canceled.",
          note:
            "Whenever you are ready, you are welcome to create a new booking.",
          details: [
            {
              label: "Confirmation Code",
              value: trimmedCode,
            },
            {
              label: "Status",
              value: "CANCELED",
            },
          ],
          actions: [
            {
              label: "Book Another Appointment",
              to: "/booking",
              variant: "primary",
            },
            {
              label: "Back to Home",
              to: "/",
              variant: "secondary",
            },
          ],
        },
      });
    } catch (err) {
      setError(err?.message || "We could not cancel your booking.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const codeFromQuery = searchParams.get("code") || "";
    if (!codeFromQuery) return;

    setCode(codeFromQuery);
  }, [searchParams]);

  return (
    <div className="page-section">
      <PageContainer width="md">
        <div className="mx-auto max-w-xl content-card-lg p-8">
          <SectionTitle
            eyebrow="Cancel Appointment"
            title="Cancel your booking"
            subtitle="Enter your confirmation code below if you would like to cancel your appointment."
            className="mb-8"
          />

          {error ? (
            <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 p-4">
              <p className="text-sm font-medium text-red-700">{error}</p>
            </div>
          ) : null}

          <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 p-4">
            <div className="flex items-start gap-3">
              <FiAlertTriangle className="mt-0.5 text-amber-600" size={20} />
              <p className="text-sm text-amber-700">
                Please use the exact confirmation code you received when your booking was created.
              </p>
            </div>
          </div>

          <form onSubmit={handleCancel} className="space-y-5">
            <Input
              label="Confirmation Code"
              name="confirmationCode"
              value={code}
              onChange={(event) => {
                setCode(event.target.value);
                setError("");
              }}
              placeholder="Enter your confirmation code"
              required
            />

            <Button type="submit" variant="danger" size="lg" fullWidth loading={loading}>
              Cancel My Booking
            </Button>
          </form>
        </div>
      </PageContainer>
    </div>
  );
};

export default BookingCancel;